/** Competitive positioning for /compare — honest claims + roadmap. */

export const COMPARE_HERO = {
  overline: "Competitive landscape · Sep 2026",
  title: "AivoryEMS vs Cvent & GEVME",
  subtitle:
    "Cvent’s September release pushes AI into RFPs and natural-language event insights. GEVME’s Coplanner remains early-access content automation. AivoryEMS competes by making AivoryONE a governed execution agent inside registration, onsite, communications, analytics — and, next, procurement tied to financial control.",
};

/** Feature matrix: values are short status labels shown in the UI. */
export const COMPARE_MATRIX = [
  {
    capability: "Registration, QR check-in & badges",
    aivory: "Bundled from Starter",
    cvent: "Strong (often modular)",
    gevme: "Strong",
  },
  {
    capability: "Singapore hosting & APAC SGD pricing",
    aivory: "Native",
    cvent: "Global / enterprise",
    gevme: "APAC-focused",
  },
  {
    capability: "No per-ticket platform fee",
    aivory: "Yes on subscription plans",
    cvent: "Varies by package",
    gevme: "Varies",
  },
  {
    capability: "In-workspace AI that executes setup",
    aivory: "AivoryONE — tools + audit",
    cvent: "Cvent Assistant / CventIQ",
    gevme: "Coplanner (early access)",
  },
  {
    capability: "Natural-language event insights",
    aivory: "Live (registration & ops)",
    cvent: "Event Insights released (Sep 2026)",
    gevme: "Analytics via Coplanner",
  },
  {
    capability: "AI RFP / vendor procurement",
    aivory: "Roadmap — EventOS chain",
    cvent: "AI RFP from Vendor Search (Sep 2026)",
    gevme: "Not a primary focus",
  },
  {
    capability: "External agent platforms (Claude, Agentforce…)",
    aivory: "Governed in-product agent first",
    cvent: "CventIQ Skills — multi-year AI investment",
    gevme: "Limited public signal",
  },
  {
    capability: "AI governance (confirm · redact · audit)",
    aivory: "Controls live — see Trust Centre",
    cvent: "Enterprise programme",
    gevme: "Vendor policy",
  },
  {
    capability: "Government / FormSG path",
    aivory: "Supported on gov workspaces",
    cvent: "Enterprise integrations",
    gevme: "Regional",
  },
];

export const AIVORYONE_LOOP = {
  overline: "AivoryONE design principle",
  title: "Understand → Analyse → Recommend → Execute → Audit",
  body: "Cvent is turning its suite into an AI interface over event data. AivoryONE is designed as a governed event-execution agent — not only a content generator.",
  stages: [
    {
      id: "understand",
      title: "Understand",
      body: "Parse briefs, intents, and workspace context before acting.",
    },
    {
      id: "analyse",
      title: "Analyse",
      body: "Inspect registration, setup gaps, and operational signals in your tenant.",
    },
    {
      id: "recommend",
      title: "Recommend",
      body: "Surface next actions and playbooks with clear human checkpoints.",
    },
    {
      id: "execute",
      title: "Execute",
      body: "Call in-product tools for events, forms, pages, agenda, speakers, and campaigns.",
    },
    {
      id: "audit",
      title: "Audit",
      body: "Prompt-hash and actor logs for AI mutations; publish/import require confirmation.",
    },
  ],
};

export const PROCUREMENT_ROADMAP = {
  overline: "EventOS procurement roadmap",
  title: "From event brief to actual expenditure",
  body: "Cvent’s AI RFP creation connects planning intent to supplier sourcing. AivoryEMS differentiates by chaining procurement into financial control — not a bolted-on vendor form.",
  chain: [
    { id: "brief", label: "Event brief", status: "live" },
    { id: "budget", label: "Budget / line items", status: "roadmap" },
    { id: "reqs", label: "Vendor requirements", status: "roadmap" },
    { id: "rfp", label: "AI RFQ / RFP", status: "draft" },
    { id: "quotes", label: "Quotations", status: "roadmap" },
    { id: "compare", label: "Comparison", status: "roadmap" },
    { id: "approve", label: "Approval", status: "roadmap" },
    { id: "award", label: "Award", status: "roadmap" },
    { id: "spend", label: "Actual expenditure", status: "roadmap" },
  ],
  footnote:
    "Draft = AI can help produce RFP/RFQ copy from an event brief today. Roadmap items are not claimed as shipped product.",
};

export const COMPETITIVE_TAKEAWAYS = [
  {
    title: "Cvent is the louder signal",
    body: "September 2026: AI RFP creation and Event Insights are released. July: 70+ innovations, 34 AI capabilities, and a multi-year ~US$1B product investment toward agent execution (CventIQ Skills).",
  },
  {
    title: "GEVME is steadier",
    body: "No major new platform change in this check. Coplanner still emphasises content, automation, and analytics in early access.",
  },
  {
    title: "AivoryEMS priority",
    body: "Make AivoryONE a governed execution agent with procurement, registration, budgeting, communications, analytics, and approvals as callable capabilities — not recommendations alone.",
  },
];
